/**
 * Declares the expected fields required for formatting a request error to a
 * user message.
 */
export type ResponseError = {
    data?: {
        success?: number;
        error?: string;
        messages?: string[];
    };
};

/**
 * Declares a result type for the `getDisplayMessageForRequestError()` function
 * (just allows us to return a tuple/pair so the caller knows how/why the
 * message is formatted this way.)
 */
export interface ResponseErrorMessageResult {
    message: string;
    hasContext: boolean;
}

export const UNKNOWN_ERROR_MESSAGE =
    'An unknown error has occurred.' +
    ' Please try again later and contact our support team if you still see this message.';
export const GENERIC_ACTION_CONTEXT_MESSAGE =
    'We encountered some problems while performing that action:';

/**
 * Create an error response display message from an error response object.
 *
 * Attempts to handle all cases gracefully with appropriate messages displayed
 * to the user:
 * - With no base error or context: Suggests next steps.
 * - With only base error: Shows base error message.
 * - With base error and context: Shows base and appends context messages
 *                                joined with commas.
 * - With only context: Shows a generic leading message and appends context
 *                      messages joined with commas.
 *
 * @param {ResponseError|object} response
 *
 * @return {ResponseErrorMessageResult}
 */
export function getDisplayMessageForRequestError(
    response: ResponseError | object
): ResponseErrorMessageResult {
    let message = UNKNOWN_ERROR_MESSAGE;
    let hasContextMessages = false;

    if ('data' in response && typeof response.data === 'object') {
        let hasBaseErrorMessage = false;
        if (
            'error' in response.data &&
            typeof response.data.error === 'string' &&
            response.data.error.length > 0
        ) {
            hasBaseErrorMessage = true;
            message = response.data.error;
        }

        // Check for list of error messages ...
        if (
            'messages' in response.data &&
            Array.isArray(response.data.messages) &&
            response.data.messages.length > 0
        ) {
            hasContextMessages = true;
            if (!hasBaseErrorMessage) {
                message = GENERIC_ACTION_CONTEXT_MESSAGE;
            }
            message += '\n' + response.data.messages.join(', ');
        }
    }

    return {
        message: message,
        hasContext: hasContextMessages,
    };
}
